import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import orderBy from 'lodash/orderBy';

import { NAMESPACE_TRANSLATION } from 'internationalise';
import { useLoggingQuery } from 'lib/apollo/apollo-hooks';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import { ListSkeleton } from 'approot/shared/skeleton/list-skeleton';
import { formatTime } from 'approot/shared/logic/dates';
import {
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerWrapper,
} from '../activity-builder-drawer';
import { GET_DRAFT_CONTENT_HISTORY } from './history.graphql';
import type {
  GetDraftContentHistoryQuery,
  GetDraftContentHistoryQueryVariables,
} from './history.graphql.generated';
import {
  getComponentTypeLabel,
  getSlideEventLabel,
  groupData,
  restoredAtMessage,
} from './history-drawer.utils';
import * as Styled from './history-drawer.style';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import { DRAFT_ACTION_RESTORE_FROM_DRAFT_HISTORY } from 'global/constants';
import { getDraftUrl } from 'approot/activity-builder/activity-builder-url.utils';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import Emoji from 'approot/shared/emoji/emoji';
import useUserData from 'approot/shared/user/user-data';
import type { Pluck } from 'lib/gql/pluck';

const ActivityBuilderHistoryDrawer = ({
  contentId,
  onClose,
}: {
  contentId: number;
  onClose: () => void;
}) => {
  const { t } = useTranslation(NAMESPACE_TRANSLATION);
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get('draftId');

  const { user } = useUserData();
  const { closeDrawer } = useActivityBuilderContext();

  const navigate = useNavigate();
  const { slug, userClassId, contentType } = useGetInteractiveLessonParams();

  const { data, loading } = useLoggingQuery<
    GetDraftContentHistoryQuery,
    GetDraftContentHistoryQueryVariables
  >(GET_DRAFT_CONTENT_HISTORY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      input: {
        contentId,
        contentType,
      },
    },
  });

  const grouped = useMemo(() => {
    const history = data?.getDraftContentHistory.drafts || [];
    const ordered = orderBy(history, 'date', 'desc');
    return groupData(ordered, t);
  }, [data?.getDraftContentHistory.drafts, t]);

  const handleItemClick = useCallback(
    (
      item: Pluck<GetDraftContentHistoryQuery, 'getDraftContentHistory.drafts'>
    ) => {
      closeDrawer();
      navigate(
        getDraftUrl({
          slug,
          userClassId: `${userClassId}`,
          draftId: item.id,
          sectionId: item.sectionId ? item.sectionId : undefined,
          contentType,
        })
      );
    },
    [slug, userClassId, closeDrawer, navigate, contentType]
  );

  return (
    <ActivityBuilderDrawerWrapper onCloseByEscape={onClose}>
      <ActivityBuilderDrawerHeader title="Draft History" onClose={onClose} />
      <ActivityBuilderDrawerContent>
        <Styled.HistoryList>
          {loading ? (
            <ListSkeleton />
          ) : (
            Object.keys(grouped)
              .filter(key => !(grouped[key].length === 0))
              .map((key, groupIndex) => {
                return (
                  <li key={key}>
                    <Styled.HistoryListTitle data-selector="history-list-title">
                      {key} ({grouped[key].length})
                    </Styled.HistoryListTitle>
                    <Styled.HistoryList data-selector="history-list">
                      {grouped[key].map((item, itemIndex) => {
                        const isCurrentDraft =
                          groupIndex === 0 && itemIndex === 0;
                        const isLatestDraftActive = !draftId && isCurrentDraft;
                        return (
                          <Styled.HistoryListItem
                            data-selector="history-list-item"
                            key={item.id}
                            $isActive={
                              isLatestDraftActive || item.id === draftId
                            }
                          >
                            <Styled.HistoryListItemTrigger
                              type="button"
                              onClick={event => {
                                isLatestDraftActive || item.id === draftId
                                  ? event.preventDefault()
                                  : handleItemClick(item);
                              }}
                              disabled={
                                isLatestDraftActive || item.id === draftId
                              }
                            >
                              <ScreenReaderOnly data-selector="history-action">
                                {item.action}
                              </ScreenReaderOnly>
                              <Styled.HistoryListItemContent aria-hidden>
                                {(isLatestDraftActive ||
                                  item.id === draftId) && (
                                  <Styled.HistoryListSubText data-selector="action-sub-text">
                                    <strong>
                                      <i>Current version</i>
                                    </strong>
                                  </Styled.HistoryListSubText>
                                )}
                                <Styled.HistoryListListText>
                                  {item.action ===
                                    DRAFT_ACTION_RESTORE_FROM_DRAFT_HISTORY &&
                                  item.restoreDraftDate
                                    ? restoredAtMessage(item.restoreDraftDate)
                                    : item.action}
                                  {item.componentType
                                    ? `: ${getComponentTypeLabel(
                                        item.componentType
                                      )}`
                                    : item.templateName
                                      ? `: ${item.templateName}`
                                      : ''}
                                </Styled.HistoryListListText>
                                <Styled.HistoryListSubText>
                                  {`${getSlideEventLabel(
                                    item.slideNumber,
                                    item.subSlideNumber
                                  )} · ${formatTime(new Date(item.date))}`}
                                </Styled.HistoryListSubText>

                                <Styled.HistoryListUser>
                                  {item.user && (
                                    <>
                                      <Emoji
                                        id={item.user.emeji || 'world'}
                                        size={20}
                                      />
                                      <strong>
                                        {user?.id === item.user.id
                                          ? 'You'
                                          : `${item.user.firstName} ${item.user.lastName}`}
                                      </strong>
                                    </>
                                  )}
                                </Styled.HistoryListUser>
                              </Styled.HistoryListItemContent>
                            </Styled.HistoryListItemTrigger>
                          </Styled.HistoryListItem>
                        );
                      })}
                    </Styled.HistoryList>
                  </li>
                );
              })
          )}
        </Styled.HistoryList>
      </ActivityBuilderDrawerContent>
    </ActivityBuilderDrawerWrapper>
  );
};

export default ActivityBuilderHistoryDrawer;
